// Cliente del módulo de operación → `/api/v1/admin/workforce`.

import { ensureFreshToken } from '@/lib/tokenRefresh';

import type {
  Absence,
  Attendance,
  ChecklistRun,
  ChecklistTemplate,
  Incident,
  IncidentOption,
  PeriodSummary,
  Task,
} from './types';

const DJANGO_PROXY = '/django-api';
const BASE = '/api/v1/admin/workforce';

export class WorkforceApiError extends Error {
  constructor(message: string, readonly status: number) {
    super(message);
    this.name = 'WorkforceApiError';
  }
}

async function authHeaders(): Promise<Record<string, string>> {
  await ensureFreshToken();
  const token = localStorage.getItem('auth_token');
  const orgId = localStorage.getItem('org_id') || '';
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (token) headers.Authorization = `Bearer ${token}`;
  if (orgId) headers['X-Org-Id'] = orgId;
  return headers;
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = await authHeaders();
  if (init.body) headers['Content-Type'] = 'application/json';
  const res = await fetch(`${DJANGO_PROXY}${BASE}${path}`, { ...init, headers });
  if (!res.ok) {
    let detail = `HTTP ${res.status}`;
    try {
      const body = await res.json();
      detail = body?.detail || detail;
    } catch {
      /* sin cuerpo JSON */
    }
    throw new WorkforceApiError(detail, res.status);
  }
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

export const workforceApi = {
  attendance: (params: { business_date?: string; person?: string } = {}): Promise<Attendance[]> => {
    const qs = new URLSearchParams();
    if (params.business_date) qs.set('business_date', params.business_date);
    if (params.person) qs.set('person', params.person);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return request(`/attendance${suffix}`);
  },
  summary: (person: string, desde: string, hasta: string): Promise<PeriodSummary> =>
    request(
      `/attendance/summary?person=${encodeURIComponent(person)}&desde=${desde}&hasta=${hasta}`,
    ),

  absences: (status?: string): Promise<Absence[]> =>
    request(`/absences${status ? `?status=${status}` : ''}`),
  decideAbsence: (id: string, approve: boolean): Promise<Absence> =>
    request(`/absences/${id}/decide`, { method: 'POST', body: JSON.stringify({ approve }) }),

  templates: (): Promise<ChecklistTemplate[]> => request('/checklist-templates'),
  createTemplate: (body: {
    name: string;
    moment: string;
    location_id: string | null;
    steps: string[];
    steps_with_photo: string[];
  }): Promise<ChecklistTemplate> =>
    request('/checklist-templates', { method: 'POST', body: JSON.stringify(body) }),

  runs: (params: { location_id?: string; business_date?: string } = {}): Promise<ChecklistRun[]> => {
    const qs = new URLSearchParams();
    if (params.location_id) qs.set('location_id', params.location_id);
    if (params.business_date) qs.set('business_date', params.business_date);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return request(`/checklist-runs${suffix}`);
  },

  tasks: (pending = true): Promise<Task[]> => request(`/tasks?pending=${pending}`),
  createTask: (body: {
    text: string;
    location_id: string;
    due_on: string;
    assigned_to: string;
  }): Promise<Task> => request('/tasks', { method: 'POST', body: JSON.stringify(body) }),

  incidentOptions: (): Promise<IncidentOption[]> => request('/incident-options'),
  createIncidentOption: (body: {
    category: string;
    text: string;
    slug: string;
    severity: string;
    notify_immediately: boolean;
  }): Promise<IncidentOption> =>
    request('/incident-options', { method: 'POST', body: JSON.stringify(body) }),

  incidents: (params: { severity?: string; unresolved?: boolean } = {}): Promise<Incident[]> => {
    const qs = new URLSearchParams();
    if (params.severity) qs.set('severity', params.severity);
    if (params.unresolved) qs.set('unresolved', 'true');
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return request(`/incidents${suffix}`);
  },
};
